import {makeAutoObservable, runInAction, reaction} from 'mobx';
import axios from 'axios';
import {settingsStore} from '@/shared/stores/settingsStore';
import type {ExpertiseTaskSummary} from '@/../generated/src/types';

class ExpertiseSummaryStore {
    private _data: ExpertiseTaskSummary | null = null;
    private _error: Error | null = null;
    private _isLoading = false;
    private _hasAttemptedLoad = false;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    get data(): ExpertiseTaskSummary | null {
        if (!this._hasAttemptedLoad) {
            this.fetch();
        }
        return this._data;
    }

    get isLoading(): boolean {
        return this._isLoading;
    }

    get error(): Error | null {
        return this._error;
    }

    observe(callback: () => void) {
        return reaction(
            () => ({
                data: this._data,
                isLoading: this._isLoading,
                error: this._error,
            }),
            () => callback(),
            {fireImmediately: true},
        );
    }

    async fetch() {
        if (this._isLoading) return;

        runInAction(() => {
            this._isLoading = true;
            this._error = null;
        });

        try {
            const headers = settingsStore.headers;
            if (!headers) {
                throw new Error('No headers available');
            }

            const response = await axios.get<ExpertiseTaskSummary>(
                `${settingsStore.apiUrl}/api/1.0/expertiseTaskSummary`,
                {headers},
            );

            runInAction(() => {
                this._data = response.data;
                this._hasAttemptedLoad = true;
                this._isLoading = false;
            });
        } catch (error) {
            runInAction(() => {
                this._error = error instanceof Error ? error : new Error('Unknown error occurred');
                this._isLoading = false;
                this._hasAttemptedLoad = true;
            });
        }
    }

    reset() {
        runInAction(() => {
            this._data = null;
            this._error = null;
            this._isLoading = false;
            this._hasAttemptedLoad = false;
        });
    }
}

export const expertiseSummaryStore = new ExpertiseSummaryStore();
